import { useAuth } from "@/hooks/use-auth";
import { useLocation, Link } from "wouter"; // Consolidated import
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { insertUserSchema } from "@shared/schema";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { motion } from "framer-motion";
import { useToast } from "@/hooks/use-toast";
import { Eye, EyeOff } from "lucide-react";
import { useState } from "react";
import { Separator } from "@/components/ui/separator";
import { useMobile } from "@/providers/mobile-provider"; 
import { EnhancedHeader } from "@/components/ui/enhanced-header";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { GameNotifications } from "@/components/ui/game-notifications";
import useGameNotifications from "@/hooks/use-game-notifications";
import { AuthLoading } from "@/components/auth/auth-loading";

export default function AuthPage() {
  const { user, loginMutation, registerMutation, googleSignIn } = useAuth();
  const { isNative } = useMobile();
  const [_, setLocation] = useLocation();
  const { notifications, showNotification, removeNotification } = useGameNotifications();
  const [showTermsDialog, setShowTermsDialog] = useState(false);

  // State for password visibility
  const [showLoginPassword, setShowLoginPassword] = useState(false);
  const [showRegisterPassword, setShowRegisterPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // Prevent spaces in username - through keyboard events and input changes
  const preventSpacesInUsername = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === ' ') {
      e.preventDefault();
    }
  };
  
  // Handle automatically inserted spaces (e.g., after double-tap period on mobile)
  const removeSpacesFromUsername = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.value.includes(' ')) {
      e.target.value = e.target.value.replace(/\s+/g, '');
    }
  };

  // Forms
  const loginForm = useForm({
    resolver: zodResolver(insertUserSchema),
    defaultValues: { username: "", password: "" },
    mode: "onChange",
  });

  const registerForm = useForm({
    resolver: zodResolver(insertUserSchema),
    defaultValues: {
      username: "",
      email: "",
      password: "",
      confirmPassword: "",
      acceptTerms: false,
    },
  });

  // If a specific auth action is in progress, show loading.
  if (loginMutation.isPending || registerMutation.isPending || googleSignIn.isPending) {
    return <AuthLoading />;
  }

  // If auth actions are NOT pending, and we have a user, redirect.
  if (user) {
    setLocation("/games");
    return <AuthLoading />; // Show loader during the redirect itself
  }

  // If we reach here, it means:
  // 1. No specific auth mutation is pending.
  // 2. User is not yet available (could be initial load or genuinely not logged in).
  //    The main App.tsx router should handle the global initial loading state.
  //    AuthPage will render its forms if the user is genuinely not logged in.

  const handleGoogleSignIn = () => {
    googleSignIn.mutate();
  };

  const handleGoogleSignUp = () => {
    if (!registerForm.watch("acceptTerms")) {
      setShowTermsDialog(true);
      return;
    }
    handleGoogleSignIn();
  };

  // Only show auth content if not authenticated
  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-950 via-gray-900 to-black text-white">
      <GameNotifications notifications={notifications} removeNotification={removeNotification} />
      {/* Enhanced header */}
      <EnhancedHeader 
        title="" 
        backPath="/"
        accentColor="text-green-500"
      />
      
      <div className="container mx-auto px-4 flex items-center justify-center" style={{ }}>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="w-full max-w-md mx-4 py-8"
        >
          <Card className="p-8 backdrop-blur-md bg-black/40 theme-border-primary/20">
            <div className="mb-8 text-center">
              <h1 className="text-4xl font-bold text-white mb-2">Spar Legends</h1>
              <p className="text-gray-300">
                Join the ultimate card gaming experience
              </p>
            </div>

            <Tabs defaultValue="login" className="space-y-6">
              <TabsList className="grid w-full grid-cols-2 bg-black/20">
                <TabsTrigger
                  value="login"
                  className="text-white data-[state=active]:theme-bg-primary"
                >
                  Login
                </TabsTrigger>
                <TabsTrigger
                  value="register"
                  className="text-white data-[state=active]:theme-bg-primary"
                >
                  Register
                </TabsTrigger>
              </TabsList>

              <TabsContent value="login">
                {/* Google Sign-in Button */}
                <div className="mb-4">
                  <Button
                    type="button"
                    variant="outline"
                    className="w-full bg-white text-gray-800 hover:bg-gray-100 border-none flex items-center justify-center gap-2"
                    onClick={handleGoogleSignIn}
                    disabled={googleSignIn.isPending}
                  >
                    {googleSignIn.isPending ? (
                      "Signing in..."
                    ) : (
                      <>
                        <svg className="h-5 w-5" viewBox="0 0 24 24">
                          <path
                            d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                            fill="#4285F4"
                          />
                          <path
                            d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                            fill="#34A853"
                          />
                          <path
                            d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                            fill="#FBBC05"
                          />
                          <path
                            d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                            fill="#EA4335"
                          />
                        </svg>
                        {/* Simplified conditional operation */}
                        Sign in with Google
                      </>
                    )}
                  </Button>
                </div>

                <div className="relative my-6">
                  <Separator className="bg-white/20" />
                  <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 bg-black/40 px-2 text-sm text-white/60">
                    OR
                  </div>
                </div>

                <Form {...loginForm}>
                  <form
                    onSubmit={async (e) => {
                      e.preventDefault();
                      const data = loginForm.getValues();
                      if (!data.username || !data.password) {
                        return;
                      }
                      loginMutation.mutate(data);
                    }}
                    className="space-y-4"
                  >
                    <FormField
                      control={loginForm.control}
                      name="username"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="text-white">Username</FormLabel>
                          <FormControl>
                            <Input
                              {...field}
                              className="bg-black/30 theme-border-primary/50 text-white"
                              placeholder="Enter your username"
                              onKeyDown={preventSpacesInUsername}
                              onChange={(e) => {
                                removeSpacesFromUsername(e);
                                field.onChange(e);
                              }}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={loginForm.control}
                      name="password"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="text-white">Password</FormLabel>
                          <FormControl>
                            <div className="relative">
                              <Input
                                type={showLoginPassword ? "text" : "password"}
                                {...field}
                                className="bg-black/30 theme-border-primary/50 text-white pr-10"
                                placeholder="Enter your password"
                              />
                              <button
                                type="button"
                                className="absolute inset-y-0 right-0 flex items-center px-3 text-gray-400 hover:text-white"
                                onClick={() =>
                                  setShowLoginPassword(!showLoginPassword)
                                }
                              >
                                {showLoginPassword ? (
                                  <EyeOff className="h-4 w-4" />
                                ) : (
                                  <Eye className="h-4 w-4" />
                                )}
                              </button>
                            </div>
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    
                    <div className="text-right">
                      <Link
                        to="/password-reset"
                        className="text-sm text-primary hover:underline"
                      >
                        Forgot password?
                      </Link>
                    </div>
                    
                    <Button
                      type="submit"
                      className="w-full theme-bg-primary hover:theme-bg-primary/90"
                      disabled={loginMutation.isPending}
                    >
                      {loginMutation.isPending ? "Logging in..." : "Login"}
                    </Button>
                  </form>
                </Form>
              </TabsContent>

              <TabsContent value="register">
                {/* Rest of the register content remains the same */}
                {/* Google Sign-up Button */}
                <div className="mb-4">
                  <Button
                    type="button"
                    variant="outline"
                    className="w-full bg-white text-gray-800 hover:bg-gray-100 border-none flex items-center justify-center gap-2"
                    onClick={handleGoogleSignUp}
                    disabled={googleSignIn.isPending}
                  >
                    {googleSignIn.isPending ? (
                      "Signing up..."
                    ) : (
                      <>
                        <svg className="h-5 w-5" viewBox="0 0 24 24">
                          <path
                            d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                            fill="#4285F4"
                          />
                          <path
                            d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                            fill="#34A853"
                          />
                          <path
                            d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                            fill="#FBBC05"
                          />
                          <path
                            d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                            fill="#EA4335"
                          />
                        </svg>
                        {isNative ? "Sign up with Google (Browser)" : "Sign up with Google"}
                      </>
                    )}
                  </Button>
                </div>
                <Dialog open={showTermsDialog} onOpenChange={setShowTermsDialog}>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>Accept Terms to Continue</DialogTitle>
                    </DialogHeader>
                    <div className="mb-4 text-sm text-gray-700 dark:text-gray-200">
                      You must accept the Terms of Service and Privacy Policy to sign up with Google.
                    </div>
                    <div className="flex items-center space-x-2 mb-4">
                      <Checkbox
                        checked={registerForm.watch("acceptTerms")}
                        onCheckedChange={(checked) => {
                          registerForm.setValue("acceptTerms", !!checked);
                        }}
                        id="accept-terms-modal"
                      />
                      <label htmlFor="accept-terms-modal" className="text-sm select-none cursor-pointer">
                        I accept the <Link to="/terms-of-service" className="text-primary hover:underline">Terms of Service</Link> and <Link to="/privacy-policy" className="text-primary hover:underline">Privacy Policy</Link>
                      </label>
                    </div>
                    <DialogFooter>
                      <Button
                        type="button"
                        disabled={!registerForm.watch("acceptTerms")}
                        onClick={() => {
                          setShowTermsDialog(false);
                          handleGoogleSignIn();
                        }}
                      >
                        Continue with Google
                      </Button>
                    </DialogFooter>
                  </DialogContent>
                </Dialog>

                <div className="relative my-6">
                  <Separator className="bg-white/20" />
                  <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 bg-black/40 px-2 text-sm text-white/60">
                    OR
                  </div>
                </div>
                
                <Form {...registerForm}>
                  <form
                    onSubmit={registerForm.handleSubmit((data) => {
                      registerMutation.mutate(data, {
                        onError: (error) => {
                          showNotification("Registration failed", error.message, "error");
                        },
                      });
                    })}
                    className="space-y-4"
                  >
                    <FormField
                      control={registerForm.control}
                      name="username"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="text-white">Username</FormLabel>
                          <FormControl>
                            <Input
                              {...field}
                              className="bg-black/30 theme-border-primary/50 text-white"
                              placeholder="Choose a username"
                              onKeyDown={preventSpacesInUsername}
                              onChange={(e) => {
                                removeSpacesFromUsername(e);
                                field.onChange(e);
                              }}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={registerForm.control}
                      name="email"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="text-white">Email</FormLabel>
                          <FormControl>
                            <Input
                              type="email"
                              {...field}
                              className="bg-black/30 theme-border-primary/50 text-white"
                              placeholder="Enter your email"
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={registerForm.control}
                      name="password"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="text-white">Password</FormLabel>
                          <FormControl>
                            <div className="relative">
                              <Input
                                type={showRegisterPassword ? "text" : "password"}
                                {...field}
                                className="bg-black/30 theme-border-primary/50 text-white pr-10"
                                placeholder="Choose a password"
                              />
                              <button
                                type="button"
                                className="absolute inset-y-0 right-0 flex items-center px-3 text-gray-400 hover:text-white"
                                onClick={() =>
                                  setShowRegisterPassword(!showRegisterPassword)
                                }
                              >
                                {showRegisterPassword ? (
                                  <EyeOff className="h-4 w-4" />
                                ) : (
                                  <Eye className="h-4 w-4" />
                                )}
                              </button>
                            </div>
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={registerForm.control}
                      name="confirmPassword"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="text-white">
                            Confirm Password
                          </FormLabel>
                          <FormControl>
                            <div className="relative">
                              <Input
                                type={showConfirmPassword ? "text" : "password"}
                                {...field}
                                className="bg-black/30 theme-border-primary/50 text-white pr-10"
                                placeholder="Confirm your password"
                              />
                              <button
                                type="button"
                                className="absolute inset-y-0 right-0 flex items-center px-3 text-gray-400 hover:text-white"
                                onClick={() =>
                                  setShowConfirmPassword(!showConfirmPassword)
                                }
                              >
                                {showConfirmPassword ? (
                                  <EyeOff className="h-4 w-4" />
                                ) : (
                                  <Eye className="h-4 w-4" />
                                )}
                              </button>
                            </div>
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={registerForm.control}
                      name="acceptTerms"
                      render={({ field }) => (
                        <FormItem className="flex flex-row items-start space-x-3 space-y-0 rounded-md border p-4 shadow">
                          <FormControl>
                            <Checkbox
                              checked={field.value}
                              onCheckedChange={field.onChange}
                            />
                          </FormControl>
                          <div className="space-y-1 leading-none">
                            <FormLabel className="text-white">
                              Accept terms and conditions
                            </FormLabel>
                            <FormMessage />
                          </div>
                        </FormItem>
                      )}
                    />
                    <p className="text-sm text-gray-400 mb-4 text-center">
                      By registering, you agree to our{" "}
                      <Link
                        to="/terms-of-service"
                        className="text-primary hover:underline"
                      >
                        Terms of Service
                      </Link>{" "}
                      and{" "}
                      <Link
                        to="/privacy-policy"
                        className="text-primary hover:underline"
                      >
                        Privacy Policy
                      </Link>
                    </p>
                    <Button
                      type="submit"
                      className="w-full theme-bg-primary hover:theme-bg-primary/90"
                      disabled={registerMutation.isPending}
                    >
                      {registerMutation.isPending
                        ? "Creating Account..."
                        : "Register"}
                    </Button>
                  </form>
                </Form>
              </TabsContent>
            </Tabs>
          </Card>
        </motion.div>
      </div>
    </div>
  );
}
